import React, { createContext, useCallback, useContext, useEffect, useId, useRef, useState } from 'react';
import './SettingsShell.css';

const SectionActive = createContext(true);
export const useSettingsSectionActive = () => useContext(SectionActive);
const focusable = host => [...host.querySelectorAll('button, input, select, textarea, a[href], [tabindex]')].filter(node => !node.disabled && node.tabIndex >= 0 && !node.closest('[hidden], [inert]') && getComputedStyle(node).display !== 'none' && getComputedStyle(node).visibility !== 'hidden' && node.getClientRects().length);

// Presentation only: services, authority, drafts and persistence belong to callers.
// A request object permits repeated links to the same destination without reads.
export default function SettingsShell({ title, context, sections, initialSection, footer, className = '', scrollClassName = '', footerClassName = '', notice, ready = true }) {
  const [mobile, setMobile] = useState(() => window.matchMedia ? window.matchMedia('(max-width: 650px)').matches : true);
  const target = typeof initialSection === 'object' ? initialSection?.section : initialSection;
  const validInitial = sections.some(s => s.id === target) ? target : null;
  const [selected, setSelected] = useState(validInitial);
  const validSelected = sections.some(s => s.id === selected) ? selected : null;
  const firstSection = sections[0]?.id;
  const active = validSelected || (!mobile ? firstSection : null);
  const shell = useRef(null);
  const scroll = useRef(null);
  const heading = useRef(null);
  const navigation = useRef(null);
  const positions = useRef({});
  const previous = useRef(null);
  const opener = useRef(document.activeElement);
  const lastFocus = useRef(null);
  const activeRef = useRef(active);
  activeRef.current = active;
  const titleId = useId();
  const navigate = useCallback(id => {
    const outgoing = activeRef.current;
    if (id !== outgoing) {
      positions.current[outgoing || 'index'] = scroll.current?.scrollTop || 0;
      previous.current = outgoing;
    }
    setSelected(id);
  }, []);
  useEffect(() => {
    const media = window.matchMedia?.('(max-width: 650px)');
    if (!media) return undefined;
    const change = () => {
      // Keep the effective desktop section when crossing to focused mobile.
      if (media.matches && activeRef.current) navigate(activeRef.current);
      else if (!media.matches && !activeRef.current) navigate(firstSection || null);
      setMobile(media.matches);
    };
    media.addEventListener('change', change);
    return () => media.removeEventListener('change', change);
  }, [navigate, firstSection]);
  useEffect(() => { if (validInitial) navigate(validInitial); }, [validInitial, initialSection, navigate]);
  useEffect(() => { if (selected && !validSelected) navigate(null); }, [selected, validSelected, navigate]);
  useEffect(() => {
    const body = scroll.current;
    const measure = () => body.style.setProperty('--settings-body-height', `${body.clientHeight}px`);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(body);
    return () => observer.disconnect();
  }, [ready]);
  useEffect(() => {
    if (!ready) return;
    if (scroll.current) scroll.current.scrollTop = positions.current[active || 'index'] || 0;
    if (active) {
      heading.current?.focus({ preventScroll: true });
      const nav = navigation.current;
      const row = nav?.querySelector(`[data-destination="${active}"]`);
      if (!mobile && row) {
        const top = row.offsetTop, bottom = top + row.offsetHeight;
        if (top < nav.scrollTop) nav.scrollTop = top;
        else if (bottom > nav.scrollTop + nav.clientHeight) nav.scrollTop = bottom - nav.clientHeight;
      }
    }
    else {
      const destination = (previous.current && navigation.current?.querySelector(`[data-destination="${previous.current}"]`)) || navigation.current?.querySelector('button');
      destination?.focus({ preventScroll: true });
    }
  }, [active, mobile, ready]);
  useEffect(() => {
    const root = shell.current;
    const ownerModal = root.closest('.modal');
    const topHost = () => {
      const modals = [...document.querySelectorAll('.modal')];
      const top = modals.at(-1);
      if (!ownerModal) return root;
      if (top === ownerModal) return top;
      // A nested settings shell owns its own trap. Locally contain other nested dialogs.
      return modals.indexOf(top) > modals.indexOf(ownerModal) && !top.querySelector('.settings-shell') ? top : null;
    };
    const onKey = event => {
      if (event.key !== 'Tab' || root.closest('[hidden]')) return;
      const host = topHost();
      if (!host) return;
      const nodes = focusable(host);
      const first = nodes[0], last = nodes.at(-1);
      if (!first) { event.preventDefault(); root.focus({ preventScroll: true }); return; }
      if (!host.contains(document.activeElement) || (event.shiftKey ? document.activeElement === first : document.activeElement === last)) {
        event.preventDefault(); (event.shiftKey ? last : first).focus({ preventScroll: true });
      }
    };
    const remember = event => { if (root.contains(event.target)) lastFocus.current = event.target; };
    document.addEventListener('keydown', onKey);
    document.addEventListener('focusin', remember);
    const observer = new MutationObserver(() => {
      if (ownerModal && document.querySelectorAll('.modal')[document.querySelectorAll('.modal').length - 1] === ownerModal && !ownerModal.contains(document.activeElement) && lastFocus.current?.isConnected && !lastFocus.current.closest('[hidden]')) lastFocus.current.focus({ preventScroll: true });
    });
    if (ownerModal?.parentElement) observer.observe(ownerModal.parentElement, { childList: true });
    const originalOpener = opener.current;
    return () => {
      observer.disconnect(); document.removeEventListener('keydown', onKey); document.removeEventListener('focusin', remember);
      if (originalOpener?.isConnected) originalOpener.focus({ preventScroll: true });
    };
  }, []);
  return <div ref={shell} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} className={`settings-shell ${className}`}>
    <header className="settings-header"><h2 id={titleId}>{title}</h2>{context && <p>{context}</p>}</header>
    <div ref={scroll} className={`settings-scroll ${scrollClassName} ${active ? 'settings-focused' : 'settings-index'}`}>
      <nav ref={navigation} className="settings-navigation" aria-label={`${title} sections`}>
        {sections.map(s => <button type="button" key={s.id} data-destination={s.id} aria-current={active === s.id ? 'page' : undefined} onClick={() => navigate(s.id)}>
          <span>{s.label}<small>{s.summary}</small>{s.dirty && <small className="settings-dirty">Unsaved</small>}</span><span aria-hidden="true">›</span>
        </button>)}
      </nav>
      <div className="settings-content" hidden={!active}>
        <button type="button" className="settings-back" onClick={() => navigate(null)}>← Back to sections</button>
        <h3 ref={heading} tabIndex={-1} className="settings-section-title">{sections.find(s => s.id === active)?.label}</h3>
        {notice}
        {sections.map(s => <SectionActive.Provider key={s.id} value={active === s.id}><div data-section={s.id} hidden={active !== s.id}>{s.content}</div></SectionActive.Provider>)}
      </div>
    </div>
    {footer && <footer className={`settings-footer ${footerClassName}`}>{footer}</footer>}
  </div>;
}
